import PropTypes from 'prop-types';
import {
  getNearestInArrayOfObjects,
  isEmptyObject,
  isNil,
  sortByProperty,
  unique,
} from '../../utils/helpers';
import { ALL_SESSIONS_LABEL, getSessionLabel } from './obeeSessions';

export const obeeTablePropTypes = PropTypes.shape({
  id: PropTypes.number,
  areaId: PropTypes.number,
  tableNumber: PropTypes.string,
  tableName: PropTypes.string,
  min: PropTypes.number,
  max: PropTypes.number,
  priority: PropTypes.number,
});

export const obeeAvailabilitySuggestionPropTypes = PropTypes.shape({
  restId: PropTypes.string,
  time: PropTypes.number,
  table: obeeTablePropTypes,
  tables: PropTypes.arrayOf(obeeTablePropTypes),
});

export const obeeAvailabilitySuggestionsPropTypes = PropTypes.shape({
  data: PropTypes.arrayOf(obeeAvailabilitySuggestionPropTypes),
  error: PropTypes.string,
  pending: PropTypes.bool,
  success: PropTypes.bool,
});

/**
 * Checks if the created booking is within the area selected
 * @param {*} booking
 * @param {*} obeeBooking
 * @param {*} session
 */
const isBookingWithinArea = (booking, obeeBooking, session) => {
  return booking.areaId === -1 || obeeBooking.areaId === session.areaId;
};

/**
 * Checks if the created booking is within the time range of the selected session
 * @param {*} obeeBooking
 * @param {*} session
 */
const isBookingWithinTime = (obeeBooking, session) => {
  return obeeBooking.time >= session.startTime && obeeBooking.time <= session.endTime;
};

/**
 * Checks if the created booking has the same date as the selected date
 * @param {*} obeeBooking
 * @param {*} booking
 * @returns
 */
const isBookingForDate = (obeeBooking, booking) => {
  return obeeBooking.date === booking.date;
};

/**
 * Checks if the created booking has the same size as the selected guests size
 * @param {*} obeeBooking
 * @param {*} booking
 * @returns
 */
const isBookingForSize = (obeeBooking, booking) => {
  return obeeBooking.size === booking.guests;
};

/**
 * Gets the created booking if the criteria are met
 * @param {*} booking
 * @param {*} obeeBooking
 * @param {string} restId
 * @param {*} session
 * @returns
 */
const getBookedSuggestion = (booking, obeeBooking, restId, session) => {
  if (isEmptyObject(obeeBooking)) {
    return null;
  }

  if (!isBookingWithinArea(booking, obeeBooking, session)) {
    return null;
  }

  if (!isBookingWithinTime(obeeBooking, session)) {
    return null;
  }

  if (!isBookingForDate(obeeBooking, booking)) {
    return null;
  }

  if (!isBookingForSize(obeeBooking, booking)) {
    return null;
  }

  return {
    restId,
    table: {
      areaId: obeeBooking.areaId,
      tableNumber: obeeBooking.tableNumber,
    },
    time: obeeBooking.time,
    waitlist: obeeBooking.status === 'waitlist',
    allAreas: booking.areaId === -1,
  };
};

/**
 * Get all available suggestions depending on `session`, if `waitlist` is `on` and if `allAreas`
 * @param {*} availabilitySuggestions
 * @param {*} session
 * @param {*} restaurant
 * @param {*} booking
 * @param {*} obeeBooking
 * @returns
 */
export const getAvailabilitySuggestions = (
  availabilitySuggestions,
  booking,
  obeeBooking,
  restaurant,
  session,
) => {
  if (!session) {
    return [];
  }

  const { startTime, endTime } = session;

  // construct a suggestion to show so a user can reselect their current booking time in the edit flow
  const bookedSuggestion = getBookedSuggestion(booking, obeeBooking, restaurant.objectId, session);

  const sessionSuggestions = availabilitySuggestions.filter(
    (availabilitySuggestion) =>
      availabilitySuggestion.time >= startTime && availabilitySuggestion.time <= endTime,
  );

  return [
    ...(isNil(bookedSuggestion) ? [] : [bookedSuggestion]), // place first so it has priority for `unique()`
    ...sessionSuggestions.map((sessionSuggestion) => ({
      ...sessionSuggestion,
      allAreas: booking.areaId === -1,
    })),
  ];
};

/**
 * Gets all the suggestions for a sessionId
 * @param {*} booking
 * @param {*} sessions
 * @param {*} availabilitySuggestions
 * @param {*} obeeBooking
 * @param {*} restaurant
 * @returns
 */
export const getAvailabilitySuggestionsForSession = (
  booking,
  sessions,
  availabilitySuggestions,
  obeeBooking,
  restaurant,
) => {
  return unique(
    sessions
      .filter((session) => {
        if (booking.sessionLabel === ALL_SESSIONS_LABEL) {
          // "All" sessions selected
          return true;
        }

        // Specific session selected
        return getSessionLabel(session.label) === booking.sessionLabel;
      })
      .flatMap((session) =>
        getAvailabilitySuggestions(
          availabilitySuggestions,
          booking,
          obeeBooking,
          restaurant,
          session,
        ),
      ),
    'time',
  ).sort((a, b) => sortByProperty(a, b, 'time'));
};

/**
 * Gets the nearest available suggestion before and after the preferredTime
 * @param {{}[]} availableSuggestions
 * @param {number} preferredTime
 * @returns
 */
export const getNearestAvailableSuggestions = (availableSuggestions, preferredTime) => {
  const nearestBeforePreferredTime = getNearestInArrayOfObjects(
    availableSuggestions,
    'time',
    preferredTime,
    '<',
  );

  const nearestAfterPreferredTime = getNearestInArrayOfObjects(
    availableSuggestions,
    'time',
    preferredTime,
    '>',
  );

  // make sure we don't we return two of the same times
  return unique([nearestBeforePreferredTime, nearestAfterPreferredTime], 'time');
};
